import type {
	OptimizationGroupReviewerItem,
	OptimizationGroupReviewModel,
} from '@/shared/generated'

import React from 'react'
import { indexBy, prop } from 'lodash/fp'
import { CheckCircle2Icon, SettingsIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { getUsername, useAllUsersQuery } from '@/entities/user'

import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Tag } from '@/shared/components/ui/tag'
import { getInitials } from '@/shared/lib/avatar-helper'
import { useModals } from '@/shared/lib/modal-manager'

const ReviewersList = ({
	review,
}: {
	review: OptimizationGroupReviewModel
}) => {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const users = useAllUsersQuery()
	const usersDict = indexBy('id', users.data)
	const renderUser = ({ id, is_approved }: OptimizationGroupReviewerItem) => {
		const name = usersDict?.[id]
			? getUsername(usersDict?.[id])
			: t('app.unknown', { defaultValue: 'Unknown' })
		const email = usersDict?.[id]?.email || ''

		return (
			<div key={id} className='flex items-center gap-2'>
				<Avatar>
					<AvatarFallback>{getInitials(name)}</AvatarFallback>
				</Avatar>
				<div>
					<div className='text-sm font-medium'>{name}</div>
					<div className='text-sm text-muted-foreground'>{email}</div>
				</div>
				<div className='ml-auto'>
					{is_approved ? (
						<Tag variant='success'>
							{t('approved', { defaultValue: 'Approved' })}
						</Tag>
					) : (
						<Tag variant='warning'>
							{t('pending', { defaultValue: 'Pending' })}
						</Tag>
					)}
				</div>
			</div>
		)
	}

	return (
		<div className='min-w-96'>
			<CardHeader>
				<div className='flex justify-between'>
					<CardTitle>{t('reviewers', { defaultValue: 'Reviewers' })}</CardTitle>
					<Button
						variant='ghost'
						size='icon-sm'
						onClick={() =>
							showModal('REVIEW_MANAGEMENT', {
								optGroupId: review.optimization_group_id,
							})
						}
					>
						<SettingsIcon />
					</Button>
				</div>
				<CardDescription>
					{t('reviewers.description', {
						defaultValue:
							'The list of users who will review the optimization group results.',
					})}
				</CardDescription>
			</CardHeader>
			<CardContent className='flex flex-col gap-4'>
				{review.reviewers.map(renderUser)}
			</CardContent>
		</div>
	)
}
export function OptimizationReviewersPreview({
	review,
}: {
	review: OptimizationGroupReviewModel
}) {
	const { t } = useTranslation()

	const approved = review.reviewers.filter(prop('is_approved'))
	const completed = approved.length === review.reviewers.length

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Badge interactive variant={completed ? 'success' : 'neutral'}>
					<CheckCircle2Icon className='size-4' />
					{`${approved?.length}/${review.reviewers?.length} ${t('completed')}`}
				</Badge>
			</PopoverTrigger>
			<PopoverContent className='w-auto p-0'>
				<ReviewersList review={review} />
			</PopoverContent>
		</Popover>
	)
}
